import { PageProps, navigate } from 'gatsby';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSiteCountry } from '@agria/theme/src/hooks/useSiteCountry';
import * as Sentry from '@sentry/gatsby';
import {
  indexPage,
  indexStep,
  stepToPageMap as stepToPageMapPetFirst,
  stepToPageMapCustomerFirst,
  topLevelSteps as topLevelStepsStandard,
  topLevelStepsIE,
} from '../const';
import { useSpeciesInfo } from '../hooks/useSpeciesInfo';
import { FormSteps } from '../types/global';
import { FormPages } from '../types/steps';
import { QuoteContext } from './QuoteContext';

interface ActiveStepContextProviderProps {
  children: React.ReactNode;
  location: PageProps['location'];
}

interface ActiveStepContextProps {
  activeStep: FormSteps;
  setActiveStep: (step: FormSteps, message?: string) => void;
}

const removeHashFromString = (str: string) => str.replace(/^#/, '');

// Cache for expensive operations
class StepMapCache {
  private cache = new Map<string, Record<string, any>>();

  private readonly MAX_SIZE = 20;

  get(key: string): Record<string, any> | undefined {
    return this.cache.get(key);
  }

  set(key: string, value: Record<string, any>): void {
    if (this.cache.size >= this.MAX_SIZE) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }
}

const stepMapCache = new StepMapCache();

export const ActiveStepContext = createContext({} as ActiveStepContextProps);
ActiveStepContext.displayName = 'ActiveStepContext';

export const ActiveStepProvider = ({
  children,
  location,
}: ActiveStepContextProviderProps) => {
  const { activeSpecies } = useContext(QuoteContext);
  const speciesInfo = useSpeciesInfo(activeSpecies!);
  const firstPageLoad = useRef(true);
  const navigationTimeoutRef = useRef<NodeJS.Timeout>();
  const { isIe } = useSiteCountry();

  // Memoize step maps to avoid recreating on every render
  const { stepToPageMap, topLevelSteps } = useMemo(() => {
    const steps = isIe ? topLevelStepsIE : topLevelStepsStandard;
    const pageMap = isIe ? stepToPageMapCustomerFirst : stepToPageMapPetFirst;

    return {
      stepToPageMap: pageMap,
      topLevelSteps: steps,
    };
  }, [isIe]);

  // Memoize the modified step map based on species
  const modifiedStepMap = useMemo(() => {
    const isEquine = speciesInfo?.name === 'Equine';
    const cacheKey = `${isIe}-${isEquine}`;

    // Check cache first
    if (stepMapCache.has(cacheKey)) {
      return stepMapCache.get(cacheKey)!;
    }

    const result = isEquine
      ? stepToPageMap
      : Object.fromEntries(
          Object.entries(stepToPageMap).filter(
            ([step]) => step !== 'activityLevel'
          )
        );

    // Cache the result
    stepMapCache.set(cacheKey, result);
    return result;
  }, [stepToPageMap, speciesInfo?.name, isIe]);

  const findFirstStepByPage = useCallback(
    (formPage: FormPages): FormSteps | undefined =>
      Object.entries(modifiedStepMap).find(
        ([, page]) => page === formPage
      )?.[0] as FormSteps,
    [modifiedStepMap]
  );

  // Memoize default step calculation
  const defaultStep: FormSteps = useMemo(
    () =>
      findFirstStepByPage(location.pathname.replace(/\//g, '') as FormPages) ||
      indexStep,
    [findFirstStepByPage, location.pathname]
  );

  const [activeStep, setActiveStepFnc] = useState<FormSteps>(defaultStep);

  // Debounced navigation to prevent rapid consecutive calls
  const performNavigation = useCallback(
    (step: FormSteps, message?: string) => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      navigationTimeoutRef.current = setTimeout(() => {
        const currentPage = stepToPageMap[step];
        const previousPage = stepToPageMap[activeStep || indexStep];

        // Batch Sentry operations for better performance
        try {
          Sentry.withScope((scope) => {
            scope.setTag('agria_journey_step', step);
            Sentry.addBreadcrumb({
              category: 'step',
              message: `Step changed to ${step} on page ${currentPage} from ${previousPage}`,
              level: 'info',
            });
          });
        } catch (error) {
          console.warn('Sentry logging failed:', error);
        }

        // Determine navigation type
        const shouldNavigateToNewPage =
          currentPage &&
          previousPage &&
          currentPage !== previousPage &&
          topLevelSteps?.includes(step);

        if (shouldNavigateToNewPage) {
          // Page navigation
          const navigationUrl = `/${
            currentPage !== indexPage ? currentPage : ''
          }`;
          navigate(navigationUrl, {
            state: { step, message },
            replace: false, // Explicit for clarity
          });
        } else {
          // Hash navigation
          const nextStep = `${currentPage}#${step}`;
          const navigationUrl = `/${currentPage !== indexPage ? nextStep : ''}`;

          navigate(navigationUrl, {
            state: { step, message },
            replace: false,
          });

          requestAnimationFrame(() => {
            const event = new HashChangeEvent('hashchange', {
              oldURL: window.location.href.replace(window.location.hash, ''),
              newURL: window.location.href,
            });
            window.dispatchEvent(event);
          });
        }
      }, 10);
    },
    [activeStep, stepToPageMap, topLevelSteps]
  );

  const setActiveStep = useCallback(
    (step: FormSteps, message?: string) => {
      // Early return for same step
      if (!step || step === activeStep) {
        return;
      }

      performNavigation(step, message);
    },
    [activeStep, performNavigation]
  );

  useEffect(() => {
    const { pathname, hash } = location;
    const currentHash = removeHashFromString(hash);

    const getPageStep: FormSteps =
      (currentHash as FormSteps) ||
      findFirstStepByPage(pathname.replace(/\//g, '') as FormPages) ||
      indexStep;

    // Only update if step actually changed
    if (getPageStep !== activeStep) {
      setActiveStepFnc(getPageStep);
    }

    firstPageLoad.current = false;
  }, [location.pathname, location.hash, findFirstStepByPage, activeStep]);

  useEffect(
    () => () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      // Clear the step map cache when component unmounts
      stepMapCache.clear();
    },
    []
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValues = useMemo(
    () => ({
      activeStep,
      setActiveStep,
    }),
    [activeStep, setActiveStep]
  );

  return (
    <ActiveStepContext.Provider value={contextValues}>
      {children}
    </ActiveStepContext.Provider>
  );
};
